import React from "react";
import ActionMenu from "../ActionMenu";
import ProgressBar from "../charts/progress/ProgressBar";
import { formatDateToLongString } from "../../../utils/date/DateUtils";
import {
  getIconSrc,
  getInitialsIconSrc,
} from "../../../utils/images/iconUtils";
import {
  getBudgetColor,
  getHeavyHittersColor,
} from "../charts/utils/dynamicColorsBar";
import CategoryPill from "../badges/CategoryPill";
import { primarySpendingCategories } from "../../../utils/constants/plaidCategoryTaxonomy";
import Tooltip from "../Tooltip";

const DataTableRow = ({
  row,
  headers,
  rowDefaults = {},
  getActionMenu,
  copy,
}) => {
  // Calculate actions safely to determine visibility
  const actions = getActionMenu ? getActionMenu(row) : [];
  const hasActions = Array.isArray(actions) && actions.length > 0;

  const renderCellContent = (header, sourceRow) => {
    const rowData = { ...rowDefaults, ...sourceRow };
    const key = header.key.toLowerCase();
    const value = String(rowData[header.key] ?? rowData[key] ?? "");

    // 1. Merchant Column
    if (key === "merchant") {
      const merchantIconSrc = rowData.merchant_icon
        ? getIconSrc(rowData.merchant_icon)
        : getInitialsIconSrc(rowData.merchant);

      return (
        <div className="merchant-table-cell">
          <div className="merchant-table-cell-main">
            <img
              src={merchantIconSrc}
              alt={rowData.merchant}
              className="merchant-table-cell-icon"
            />
            <span className="merchant-table-cell-name">{value}</span>
          </div>
          {header.showAmount && rowData.amount ? (
            <span className="merchant-table-cell-amount">{rowData.amount}</span>
          ) : null}
        </div>
      );
    }

    // 2. Category Pills
    if (header.type === "category") {
      return (
        <CategoryPill
          label={value}
          categoryCode={rowData.category_code || rowData.category_value}
          categoryPrimary={rowData.category_primary}
          categoryDetailed={rowData.category_detailed}
        />
      );
    }

    if (header.type === "categories") {
      const categories = Array.isArray(rowData.categories)
        ? rowData.categories
        : [];

      if (categories.length === 0) {
        return <span className="standard-data-table-description">-</span>;
      }

      const getCategoryLabel = (code) => {
        const normalized = String(code).toUpperCase();
        const match = primarySpendingCategories.find(
          (category) => category.value === normalized,
        );
        return match?.label || code;
      };

      return (
        <div className="category-pills-cell">
          {categories.map((code) => (
            <CategoryPill
              key={code}
              label={getCategoryLabel(code)}
              categoryCode={code}
              categoryPrimary={code}
            />
          ))}
        </div>
      );
    }

    if (header.type === "description") {
      return (
        <span className="standard-data-table-description">{value || "-"}</span>
      );
    }

    // 3. Tag/Amount Pills
    if (header.type === "tag" || key === "amount" || key === "frequency") {
      const isNegative = rowData.amount_type === "sp";
      const pillClass =
        key === "amount"
          ? `amount-pill ${isNegative ? "amount-negative" : "amount-positive"}`
          : "pill-tag";
      return <span className={pillClass}>{value}</span>;
    }

    // 4. Type Column
    if (key === "recurrent_type") {
      const typeClass = `type-pill type-${value
        .toLowerCase()
        .replace(/\s+/g, "-")}`;
      return <span className={typeClass}>{value}</span>;
    }

    // 5. Date
    if (key === "date") {
      const longDate = formatDateToLongString(value); // e.g., "June 10, 2026"

      // Split by comma to isolate the year safely
      if (longDate && longDate.includes(",")) {
        const [monthDay, year] = longDate.split(",");
        const trimmedYear = year.trim(); // "2026"

        return (
          <Tooltip title="Date" content={`Full date: ${longDate}`}>
            <span
              className="light-color"
              style={{
                borderBottom: "1px dashed #A3AED0",
                cursor: "help",
                display: "inline-block",
              }}
            >
              {monthDay}
            </span>
          </Tooltip>
        );
      }

      return longDate; // Fallback if formatting doesn't contain a comma
    }

    // 6. Spent & Limit
    if (key === "spentlimit") {
      const spent = parseFloat(rowData.spent) || 0;
      const limit = parseFloat(rowData.limit) || 1;
      const threshold = parseFloat(rowData.threshold) || 1;

      // Extract trend data points safely from payload row
      const trend = rowData.trend ? String(rowData.trend).toLowerCase() : "";
      const percentageChange = Math.abs(
        parseFloat(rowData.percentage_change) || 0,
      );
      const previousSpent = parseFloat(rowData.previous_spent) || 0;
      const spentAmount = `$${spent.toLocaleString()}`;
      const previousSpentAmount = `$${previousSpent.toLocaleString()}`;
      const limitAmount = `$${limit.toLocaleString()}`;

      // Determine colors and contextual labels based on the variant flag
      let dynamicColor;
      let contextLabel = null;

      if (header.variant === "heavy_hitters") {
        // Use our new dynamic trend function
        dynamicColor = getHeavyHittersColor(spent, limit, threshold, trend);

        let trendText = "You spent the same amount as last month";
        if (trend === "up") {
          // If percentage is 0, null, or previous spent was 0, treat it as brand new spending
          if (!percentageChange || previousSpent === 0) {
            trendText = "New spending category this month";
          } else {
            trendText = `${percentageChange}% more than last month`;
          }
        } else if (trend === "down") {
          // Fallback check just in case down has an anomaly
          if (!percentageChange) {
            trendText = "Less spending than last month";
          } else {
            trendText = `${percentageChange}% less than last month`;
          }
        }

        contextLabel = (
          <span
            className="trend-context-label"
            style={{ fontSize: "12px", color: dynamicColor, marginLeft: "6px" }}
          >
            {trendText}
          </span>
        );
      } else {
        // Fallback to legacy default budget color function
        dynamicColor = getBudgetColor(spent, limit, threshold);
      }

      // Safe wrapper wrapper matching your ProgressBar custom signature requirements
      const handleGetProgressBarColor = () => dynamicColor;

      return (
        <div className="table-spent-limit-cell">
          <div
            className="spent-limit-text"
            style={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            {/* Spent amount tooltip */}
            <Tooltip
              title={copy?.category_spending_title}
              content={copy?.category_spending_description
                ?.replace("{category_amount}", spentAmount)
                .replace("{previous_category_amount}", previousSpentAmount)}
            >
              <span
                className="spent-amount"
                style={{
                  color: dynamicColor,
                  fontWeight: "600",
                  borderBottom: `1px dashed ${dynamicColor}`,
                }}
              >
                {spentAmount}
              </span>
            </Tooltip>

            <span className="separator-text"> of </span>

            {/* Total Limit Tooltip featuring comparative context if heavy_hitters variant */}
            <Tooltip
              title={
                header.variant === "heavy_hitters"
                  ? copy?.current_spending_title
                  : copy?.budget_limit_title || header.label
              }
              content={
                header.variant === "heavy_hitters"
                  ? copy?.current_spending_description
                  : copy?.budget_limit_description?.replace(
                      "{budget_limit}",
                      limitAmount,
                    )
              }
            >
              <span
                className="limit-amount"
                style={{
                  color: "#001d38",
                  borderBottom: "1px dashed #A3AED0",
                }}
              >
                {limitAmount}
              </span>
            </Tooltip>

            {/* Context label text element matches criteria conditional to variant */}
            {contextLabel}
          </div>

          <div
            className="table-progress-bar-wrapper"
            style={{ marginTop: "6px" }}
          >
            <ProgressBar
              spent={spent}
              total={limit}
              getColor={handleGetProgressBarColor}
            />
          </div>
        </div>
      );
    }

    // 7 Filing Status
    if (header.type === "filing_status") {
      const statusValue = row[header.key];
      const norm = (statusValue || "").toUpperCase();

      let badgeStyle = {
        padding: "6px 14px",
        borderRadius: "4px",
        display: "inline-block",
        fontWeight: "500",
        fontSize: "13px",
        textAlign: "center",
        minWidth: "90px",
        backgroundColor: "#E6E6E7",
        color: "#666B6F",
      };

      if (norm === "COMPLETED") {
        badgeStyle.backgroundColor = "#A6E510";
        badgeStyle.color = "#00080F";
      } else if (norm === "IN PROGRESS") {
        badgeStyle.backgroundColor = "#43A5FF";
        badgeStyle.color = "#FFF";
      } else if (norm === "ACTIVE") {
        badgeStyle.backgroundColor = "#FFD011";
        badgeStyle.color = "#00080F";
      } else if (norm === "CANCELLED") {
        badgeStyle.backgroundColor = "#FFE5E5";
        badgeStyle.color = "#CC0000";
      }

      return React.createElement(
        "span",
        { style: badgeStyle, className: "data-table-status" },
        statusValue,
      );
    }

    // 8 Payment Status
    if (header.type === "payment_status") {
      const statusValue = row[header.key];
      const norm = (statusValue || "").toUpperCase();

      let badgeStyle = {
        padding: "6px 14px",
        borderRadius: "4px",
        display: "inline-block",
        fontWeight: "500",
        fontSize: "13px",
        textAlign: "center",
        minWidth: "90px",
        backgroundColor: "#E6E6E7",
        color: "#666B6F",
      };

      if (norm === "PAID") {
        badgeStyle.backgroundColor = "#A6E510";
        badgeStyle.color = "#00080F";
      } else if (norm === "OPEN") {
        badgeStyle.backgroundColor = "#FFD011";
        badgeStyle.color = "#00080F";
      } else if (norm === "UNCOLLECTIBLE") {
        badgeStyle.backgroundColor = "#FFE5E5";
        badgeStyle.color = "#CC0000";
      }

      return React.createElement(
        "span",
        { style: badgeStyle, className: "data-table-status" },
        statusValue,
      );
    }

    // 9 Subscription Status
    if (header.type === "subscription_status") {
      const statusValue = row[header.key];
      const norm = (statusValue || "").toUpperCase();

      let badgeStyle = {
        padding: "6px 14px",
        borderRadius: "4px",
        display: "inline-block",
        fontWeight: "500",
        fontSize: "13px",
        textAlign: "center",
        minWidth: "90px",
        backgroundColor: "#E6E6E7",
        color: "#666B6F",
      };

      if (norm === "ACTIVE") {
        badgeStyle.backgroundColor = "#A6E510";
        badgeStyle.color = "#00080F";
      } else if (norm === "CANCELLED" || norm === "CANCELED") {
        badgeStyle.backgroundColor = "#FFE5E5";
        badgeStyle.color = "#CC0000";
      } else if (norm === "CANCELLING" || norm === "ENDING SOON") {
        badgeStyle.backgroundColor = "#FFF3CD";
        badgeStyle.color = "#856404";
      } else if (norm === "DUE") {
        badgeStyle.backgroundColor = "#FFD011";
        badgeStyle.color = "#00080F";
      } else if (norm === "NON-RENEWING") {
        // Mismo color de fondo y letra para mantener la armonía de la tabla
        badgeStyle.backgroundColor = "#FFF3CD";
        badgeStyle.color = "#856404";

        // Retornamos el elemento exclusivo envuelto en el Tooltip
        return (
          <span style={badgeStyle} className="data-table-status">
            <Tooltip
              title="Subscription Update"
              content="This subscription has been canceled. You will retain full access to all features and associated data until the end of your paid billing cycle, after which it will not renew."
            >
              <span
                style={{
                  borderBottom: "1px dashed #856404",
                  fontWeight: "600",
                  cursor: "help",
                }}
              >
                {statusValue}
              </span>
            </Tooltip>
          </span>
        );
      }

      // Fallback normal para todos los demás estados sin tooltip
      return React.createElement(
        "span",
        { style: badgeStyle, className: "data-table-status" },
        statusValue,
      );
    }

    return value;
  };

  return (
    <div
      className={`standard-data-table-row-wrapper ${
        !hasActions ? "no-actions-layout" : ""
      }`}
    >
      <div className="standard-data-table-content-row">
        {headers.map((header, colIndex) => (
          <div
            key={colIndex}
            className="standard-data-table-content-row-field"
            style={{
              flex: `0 0 ${header.width}`,
              width: header.width,
            }}
          >
            {renderCellContent(header, row)}
          </div>
        ))}
      </div>

      {/* FIX: Hide completely if no action arrays exist */}
      {hasActions && (
        <div className="standard-table-fixed-row-right-element">
          <ActionMenu actions={actions} />
        </div>
      )}
    </div>
  );
};

export default DataTableRow;
